/* eslint-disable @next/next/no-before-interactive-script-outside-document */
// Polyfill File for SSR (required for Chakra v3 / @zag-js compatibility)
import "@/lib/polyfills/file-polyfill";

// Emotion cache for SSR
import type { EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

// CSS imports - order matters for cascade layers
import "@/styles/grecaptcha.css";
import "@/styles/nprogress.css";
import "@ui-kit/form/DatePicker/DatePicker.style.css";
// eslint-disable-next-line import/no-unresolved
import "cropperjs/dist/cropper.css";
import "react-day-picker/src/style.css";
import "react-quill/dist/quill.snow.css";
import "swiper/swiper-bundle.min.css";
import "@shared/styles/swiper-overrides.css";
import "@puckeditor/core/puck.css";
import "@shared/modules/websites/puck-editor/puck-theme-overrides.css";
import "@shared/theme/styles/globals.css";

import Template from "@ui-application/layout/Template/Template.web";
import Footer from "@ui-application/navigation/footer/Footer/Footer.web";
import NavBar from "@ui-application/navigation/NavBar/NavBar.web";

import { UserSessionAuthStateProvider } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { DesktopAgentProvider } from "@shared/contexts/DesktopAgentContext/DesktopAgentContext";

import { API_URL } from "@app/core/src/shared/env";
import Box from "@ui-kit/layout/Box/Box.web";
import { AnalyticsContextProvider } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import { useAndroidTWACheck } from "@shared/hooks/platform/useAndroidTWACheck";
import {
  MobileWebviewProvider,
  WebviewConfig,
  parseWebviewConfig,
} from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";
import "@shared/lib/analytics/loader/gtag";
import { runCookieCleanup } from "@shared/utils/cookies/cookie-cap";
import { setDateLocaleOverride } from "@shared/utils/dates/locale";
import GlobalBasketObserver from "@shared/modules/basket/components/GlobalBasketObserver/GlobalBasketObserver.web";
import { setAxiosDefaults } from "@shared/services/axios/utils";
import { TimerProvider } from "@ui-kit/feedback/timer";
import Cookies from "js-cookie";
// Remaining next-app code still calls moment().format(); without this, fr routes get English month/day names.
// Remove this import block and moment.locale() below once moment is fully migrated out of next-app.
import moment from "moment";
import "moment/locale/fr";
import { NextPage } from "next";
import { appWithTranslation } from "next-i18next";
import App, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import Script from "next/script";
import nprogress from "nprogress";
import type { ReactElement, ReactNode } from "react";
import { useEffect } from "react";
import "react-dates/lib/css/_datepicker.css";
import "react-phone-input-2/lib/bootstrap.css";
import { Hydrate } from "react-query/hydration";

import cdn from "@shared/utils/urls/cdn";
import { PageThemeConfigProvider } from "@shared/contexts/PageThemeConfigContext/PageThemeConfigContext";
import { usePublicBasket } from "@app/core/src/app-contexts/public/features/basket/services/usePublicBasket";
import ErrorAppCrash from "@app/core/src/shared/components/application/other/ErrorAppCrash/ErrorAppCrash.web";
import ErrorBoundary from "@app/core/src/shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import { BasketServiceProvider } from "@app/core/src/shared/modules/basket/context/BasketServiceContext";
import { WaffleProvider } from "@shared/contexts/WaffleContext/WaffleContext";
import DatadogRUM from "@app/core/src/shared/components/datadog/datadog-rum";
import DatadogWebVitals from "@app/core/src/shared/components/datadog/datadog-web-vitals";
import DatadogUserContext from "@app/core/src/shared/components/datadog/datadog-user-context";

import {
  queryClient,
  QueryClientProvider,
} from "@app/core/src/shared/services/api/utils/GlobalQueryClient";
import { GlobalAuthorizationProvider } from "@app/core/src/shared/modules/auth/contexts/GlobalAuthorizationProvider/GlobalAuthorizationProvider";
import ThemeProvider from "@/ThemeProvider";

import { getClientEmotionCache } from "@/lib/createEmotionCache";

setAxiosDefaults();

if (typeof window !== "undefined") {
  runCookieCleanup();
}

export type Layout = {
  getLayout?: (page: ReactElement) => ReactNode;
};
export type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  emotionCache?: EmotionCache;
  nonce?: string;
  webviewConfig?: WebviewConfig;
};

const MyApp = ({
  Component,
  // Temporary workaround for issue https://github.com/vercel/next.js/issues/40372
  pageProps: props,
  emotionCache,
  nonce,
  webviewConfig,
}: AppPropsWithLayout): JSX.Element => {
  // Use SSR cache if provided, otherwise get the client singleton
  const cache = emotionCache ?? getClientEmotionCache();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageProps: any = props;
  const router = useRouter();

  // Initialize TWA detection hook
  useAndroidTWACheck();

  // initial set a locale based on router.locale so web-app knows what locale to use,
  // default is English locale
  const { locale = "en" } = router;

  moment.locale(locale);
  Cookies.set("NEXT_LOCALE", locale);
  setDateLocaleOverride(locale);

  // Nprogress route changing loading bar
  useEffect(() => {
    if (typeof window === undefined) {
      return;
    }

    const delay = 500; // don't show loading bar unless route change takes longer than delay
    let timer: number;

    const load = () => {
      timer = window.setTimeout(() => {
        nprogress.start();
      }, delay);
    };

    const stop = () => {
      clearTimeout(timer);
      nprogress.done();
    };

    Router.events.on("routeChangeStart", () => load());
    Router.events.on("routeChangeComplete", () => stop());
    Router.events.on("routeChangeError", () => stop());
  }, []);

  // Use the layout defined at the page level, if available
  const getLayout =
    Component.getLayout ||
    ((page) => {
      if (router.pathname.startsWith("/manage")) {
        return page;
      }
      return (
        <>
          <NavBar />
          <Box flex={1}>{page}</Box>
          <Footer />
        </>
      );
    });

  return (
    <CacheProvider value={cache}>
      <Head>
        <link
          key="favicon-ico"
          rel="icon"
          type="image/x-icon"
          href={cdn("favicon/favicon.ico")}
        />
        <link
          key="favicon-16"
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={cdn("favicon/favicon-16x16.png")}
        />
        <link
          key="favicon-32"
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={cdn("favicon/favicon-32x32.png")}
        />
      </Head>
      <Script
        strategy="beforeInteractive"
        src={`${API_URL}wafflejs`}
        crossOrigin="use-credentials"
      />

      <MobileWebviewProvider webviewConfig={webviewConfig}>
        <ErrorBoundary fallback={<ErrorAppCrash />}>
          <DesktopAgentProvider>
            <BasketServiceProvider basketService={usePublicBasket}>
              <WaffleProvider>
                <AnalyticsContextProvider enableAnalytics>
                  <PageThemeConfigProvider
                    initialThemeConfig={pageProps.initialThemeConfig}
                  >
                    <ThemeProvider
                      nonce={nonce}
                      websiteThemeConfig={pageProps.websiteThemeConfig}
                    >
                      <DatadogRUM />
                      <DatadogWebVitals />
                      <QueryClientProvider client={queryClient}>
                        {/* Hydrate MUST be the first child of QueryClientProvider that makes use of react-query.
                          When this is not the case, then components (UserSessionAuthStateProvider) access the query cache
                          before it has been hydrated. This leads to mismatches between server-rendered HTML and 
                          client-side rendering.  */}
                        <Hydrate state={pageProps.dehydratedState}>
                          <TimerProvider>
                            <UserSessionAuthStateProvider
                              hasWebviewTokenAuth={Boolean(
                                webviewConfig?.authToken
                              )}
                            >
                              <DatadogUserContext />
                              <GlobalAuthorizationProvider>
                                <GlobalBasketObserver />
                                <Template>
                                  {getLayout(<Component {...pageProps} />)}
                                </Template>
                              </GlobalAuthorizationProvider>
                            </UserSessionAuthStateProvider>
                          </TimerProvider>
                        </Hydrate>
                      </QueryClientProvider>
                    </ThemeProvider>
                  </PageThemeConfigProvider>
                </AnalyticsContextProvider>
              </WaffleProvider>
            </BasketServiceProvider>
          </DesktopAgentProvider>
        </ErrorBoundary>
      </MobileWebviewProvider>
    </CacheProvider>
  );
};

// Get nonce and webview config from request headers
MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const nonce = appContext.ctx.req?.headers["x-nonce"] as string | undefined;
  const cookie = appContext.ctx.req?.headers.cookie ?? "";
  const webviewConfig = parseWebviewConfig(cookie);
  return { ...appProps, nonce, webviewConfig };
};

export default appWithTranslation(MyApp);
